import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { adminApi } from '../../api/admin';
import { useUiStore } from '../../store/uiStore';
import { Spinner } from '../../components/Spinner';
import type { UserProfile } from '../../types';

export function AdminUsers() {
  const pushToast = useUiStore((s) => s.pushToast);

  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const load = useCallback(
    async (searchTerm: string) => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const res = await adminApi.listUsers(searchTerm || undefined);
        setUsers(res.items);
        setNextCursor(res.nextCursor);
      } catch (err) {
        setErrorMsg(
          err instanceof Error ? err.message : 'Failed to load users.',
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(activeSearch);
  }, [load, activeSearch]);

  async function loadMore() {
    if (nextCursor === null) return;
    setLoadingMore(true);
    try {
      const res = await adminApi.listUsers(
        activeSearch || undefined,
        nextCursor,
      );
      setUsers((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      pushToast(
        'error',
        err instanceof Error ? err.message : 'Failed to load more.',
      );
    } finally {
      setLoadingMore(false);
    }
  }

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    setActiveSearch(search.trim());
  }

  async function runAction(label: string, action: () => Promise<unknown>) {
    try {
      await action();
      pushToast('success', `${label} succeeded.`);
      await load(activeSearch);
    } catch (err) {
      pushToast(
        'error',
        err instanceof Error ? err.message : `${label} failed.`,
      );
    }
  }

  function handleBanToggle(user: UserProfile) {
    void runAction(user.banned ? 'Unban' : 'Ban', () =>
      user.banned ? adminApi.unbanUser(user.uid) : adminApi.banUser(user.uid),
    );
  }

  function handleAdjust(user: UserProfile) {
    const amountStr = window.prompt(
      `Adjust balance for ${user.displayName} (positive to credit, negative to debit):`,
    );
    if (amountStr === null) return;
    const amount = Number(amountStr);
    if (!Number.isInteger(amount) || amount === 0) {
      pushToast('error', 'Amount must be a non-zero integer.');
      return;
    }
    const reason = window.prompt('Reason for this adjustment:');
    if (reason === null) return;
    if (!reason.trim()) {
      pushToast('error', 'A reason is required.');
      return;
    }
    void runAction('Balance adjustment', () =>
      adminApi.adjustBalance(user.uid, amount, reason.trim()),
    );
  }

  function handleMakeAdmin(user: UserProfile) {
    if (
      !window.confirm(
        `Grant admin privileges to ${user.displayName}? This cannot be undone here.`,
      )
    )
      return;
    void runAction('Make admin', () => adminApi.createAdmin(user.uid));
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-100">Users</h1>

      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          className="input"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="btn-gold whitespace-nowrap">
          Search
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : errorMsg ? (
        <p className="card text-sm text-rose-400">{errorMsg}</p>
      ) : users.length === 0 ? (
        <p className="card text-sm text-slate-400">No users found.</p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-slate-400">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.uid}
                  className="border-b border-white/5 align-middle"
                >
                  <td className="py-2 pr-3 text-slate-100">
                    {user.displayName}
                  </td>
                  <td className="py-2 pr-3 text-slate-300">
                    {user.email ?? '—'}
                  </td>
                  <td className="py-2 pr-3 capitalize text-slate-300">
                    {user.role}
                  </td>
                  <td className="py-2 pr-3">
                    {user.banned ? (
                      <span className="text-rose-400">Banned</span>
                    ) : (
                      <span className="text-emerald-400">Active</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn-ghost px-2 py-1 text-xs"
                        onClick={() => handleBanToggle(user)}
                      >
                        {user.banned ? 'Unban' : 'Ban'}
                      </button>
                      <button
                        type="button"
                        className="btn-ghost px-2 py-1 text-xs"
                        onClick={() => handleAdjust(user)}
                      >
                        Adjust balance
                      </button>
                      {user.role !== 'admin' && (
                        <button
                          type="button"
                          className="btn-ghost px-2 py-1 text-xs"
                          onClick={() => handleMakeAdmin(user)}
                        >
                          Make admin
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor !== null && !loading && (
        <button
          type="button"
          className="btn-ghost w-full"
          onClick={loadMore}
          disabled={loadingMore}
        >
          {loadingMore ? <Spinner /> : 'Load more'}
        </button>
      )}
    </div>
  );
}
